import {
  ShieldUser,
  Menu,
  Bell,
  X,
  Maximize,
  Minimize,
  LogOut,
  User,
} from "lucide-react";
import { useState, useEffect } from "react";
import Sidebar from "./sidebar";
import { useNavigate } from "react-router-dom";
import { Button } from "../../ui/button";
import { showSuccessToast } from "../../../utils/toast-utils";
import { doAdminLogout } from "../../../services/admin/adminAuthService";
import useAdminProfile from "../../../store/admin/profile";
import { getAllCompany } from "../../../services/admin/manageCompanyServices";

const Header = () => {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [isFullScreen, setIsFullScreen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [companies, setCompanies] = useState(null);

  const { profileData, isProfileClicked, setIsProfileClicked } =
    useAdminProfile();

  // Add a subtle shadow on scroll
  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 10);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const handleLogout = async () => {
    try {
      const res = await doAdminLogout();
      if (res && (res.status === 200 || res?.data?.status === 200)) {
        showSuccessToast("Logged out successfully");
        navigate("/admin-login", { replace: true });
      }
    } catch (error) {
      console.error(error);
    }
  };

  const toggleFullScreen = () => {
    if (!document.fullscreenElement) {
      document.documentElement.requestFullscreen();
      setIsFullScreen(true);
    } else {
      document.exitFullscreen();
      setIsFullScreen(false);
    }
  };

  useEffect(() => {
    const fetchCompanies = async () => {
      try {
        const res = await getAllCompany();
        if (res?.success) {
          const list = res.data?.data || res.data;
          setCompanies(Array.isArray(list) ? list : []);
        }
      } catch (error) {
        console.log(error);
      }
    };

    fetchCompanies();
  }, []);

  return (
    <>
      <header
        className={`sticky top-0 z-40 w-full transition-all duration-300 border-b border-white/10 
        ${scrolled ? "bg-[#081a3a]/95 backdrop-blur-md shadow-lg" : "bg-[#081a3a]"} 
        text-white px-4 md:px-8 py-0 flex justify-between items-center`}
      >
        {/* Left Section: Logo/Branding */}
        <div
          className="flex items-center gap-3 group cursor-pointer"
          onClick={() => navigate("/admin-dashboard")}
        >
          <div className=" relative py-1 rounded-lg group-hover:bg-blue-500/30 transition-colors">
            {companies?.[0]?.companyLogo ? (
              <img
                src={
                  process.env.REACT_APP_IMAGE_URL + companies?.[0]?.companyLogo
                }
                alt="Company Logo"
                className="w-14 h-14 md:w-16 md:h-16 rounded-xl object-contain "
              />
            ) : (
              <ShieldUser className="text-blue-400" size={24} />
            )}
          </div>
          <div className="flex flex-col gap-1">
            <span className="font-bold text-lg tracking-wider leading-none">
              {companies?.[0]?.companyName.replace(/_/g, " ")}
            </span>
            <span className="text-[10px] uppercase tracking-widest text-blue-400 font-semibold">
              Admin Portal
            </span>
          </div>
        </div>

        {/* Right Section: Desktop Actions */}
        <div className="hidden md:flex items-center gap-4">
          <div className="flex items-center gap-2 mr-4 border-r border-white/10 pr-4">
            {/* Notification Bell with Badge */}
            <button className="relative p-2 hover:bg-white/10 rounded-full transition-all group">
              <Bell
                size={20}
                className="text-slate-300 group-hover:text-yellow-400"
              />
              <span className="absolute top-1.5 right-1.5 w-2 h-2 bg-red-500 rounded-full border-2 border-[#081a3a]"></span>
            </button>

            {/* Fullscreen Toggle */}
            <button
              onClick={toggleFullScreen}
              className="p-2 hover:bg-white/10 rounded-full transition-all text-slate-300"
              title="Toggle Fullscreen"
            >
              {isFullScreen ? <Minimize size={20} /> : <Maximize size={20} />}
            </button>
          </div>

          {/* Profile & Logout */}
          <div className="flex items-center gap-3 pl-2">
            <button
              type="button"
              className="flex items-center gap-3"
              onClick={() => setIsProfileClicked()}
            >
              <div className="flex items-center gap-3 px-4 py-1">
                <div className="w-8 h-8 rounded-full bg-blue-500 flex items-center justify-center text-xs font-bold text-white">
                  AD
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold text-white truncate">
                    {profileData?.name}
                  </p>
                  <p className="text-xs text-slate-500 truncate">Main Branch</p>
                </div>
              </div>
            </button>
            <Button
              onClick={handleLogout}
              variant="destructive"
              className="ml-2 h-9 px-4 bg-red-500/10 hover:bg-red-500 hover:text-white text-red-400 border border-red-500/20 transition-all flex gap-2"
            >
              <LogOut size={16} />
              <span className="text-xs font-semibold">Logout</span>
            </Button>
          </div>
        </div>

        {/* Mobile Menu Button */}
        <div className="md:hidden">
          <Button
            variant="ghost"
            className="p-2 hover:bg-white/10"
            onClick={() => setOpen(true)}
          >
            <Menu className="text-white" size={28} />
          </Button>
        </div>
      </header>

      {/* Mobile Drawer Overlay */}
      {open && (
        <div
          className="fixed inset-0 bg-slate-950/60 backdrop-blur-sm z-50 md:hidden transition-opacity"
          onClick={() => setOpen(false)}
        />
      )}

      {/* Mobile Sidebar Drawer */}
      <div
        className={`fixed top-0 left-0 h-full w-72 bg-[#081a3a] text-white z-[60] shadow-2xl transform transition-transform duration-300 ease-in-out md:hidden border-r border-white/10
        ${open ? "translate-x-0" : "-translate-x-full"}`}
      >
        <div className="flex justify-between items-center p-2 border-b border-white/10">
          <div className="flex items-center gap-2">
            <div className=" relative py-1 rounded-lg group-hover:bg-blue-500/30 transition-colors">
              {companies?.[0]?.companyLogo ? (
                <img
                  src={
                    process.env.REACT_APP_IMAGE_URL +
                    companies?.[0]?.companyLogo
                  }
                  alt="Company Logo"
                  className="w-14 h-14 rounded-xl object-contain "
                />
              ) : (
                <ShieldUser className="text-blue-400" size={24} />
              )}
            </div>
            <div className="flex flex-col gap-1">
              <span className="font-bold text-lg tracking-wider leading-none">
                {companies?.[0]?.companyName.replace(/_/g, " ")}
              </span>
              <span className="text-[10px] uppercase tracking-widest text-blue-400 font-semibold">
                Admin Portal
              </span>
            </div>
          </div>
          <button
            onClick={() => setOpen(false)}
            className="p-2 hover:bg-white/10 rounded-full transition-colors"
          >
            <X size={24} />
          </button>
        </div>

        <div className="p-4 overflow-y-auto h-[calc(100%-80px)]">
          <Sidebar setOpen={setOpen} isPhone={true} />
        </div>
      </div>
    </>
  );
};

export default Header;
